import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, TrendingUp, Calendar as CalIcon } from 'lucide-react';

const PerformanceCalendar = ({ history }) => {
    // Generate last 35 days (5 weeks) for the calendar grid
    const days = Array.from({ length: 35 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (34 - i));
        return {
            date: d.toISOString().split('T')[0],
            dayNum: d.getDate(),
            dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
            isToday: i === 34,
            // Mock data for demonstration until backend is fully hooked up
            score: Math.random() > 0.7 ? Math.floor(Math.random() * 5) : 0,
            pnl: Math.random() > 0.8 ? (Math.random() * 10 - 2).toFixed(1) : null
        };
    });

    // Stats
    const totalSwipes = history.length;
    const currentStreak = 3; // Mock
    const bestStreak = 12;   // Mock

    return (
        <div className="w-full max-w-2xl mx-auto p-6">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-3xl font-light text-gray-900">Your Performance</h2>
                    <p className="text-sm text-gray-400 mt-1">Consistency is your edge.</p>
                </div>
                <div className="flex gap-4">
                    <div className="text-center">
                        <div className="text-2xl font-light text-cyan-600">{currentStreak}</div>
                        <div className="text-xs text-gray-400 uppercase tracking-wider">Day Streak</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-light text-gray-900">{bestStreak}</div>
                        <div className="text-xs text-gray-400 uppercase tracking-wider">Best</div>
                    </div>
                </div>
            </div>

            {/* Calendar Grid */}
            <div className="bg-white/50 backdrop-blur-xl rounded-3xl p-6 border border-white/50 shadow-sm">
                <div className="grid grid-cols-7 gap-3 mb-2">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                        <div key={i} className="text-center text-xs font-medium text-gray-300 mb-2">
                            {day}
                        </div>
                    ))}

                    {days.map((day) => (
                        <motion.div
                            key={day.date}
                            className={`
                                aspect-square rounded-xl flex items-center justify-center relative group cursor-pointer
                                ${day.isToday ? 'ring-2 ring-cyan-500 ring-offset-2' : ''}
                                ${day.score > 0 ? 'bg-gradient-to-br from-cyan-400 to-blue-500 text-white shadow-md' : 'bg-gray-100/50 text-gray-300'}
                            `}
                            whileHover={{ scale: 1.1, zIndex: 10 }}
                            initial={day.isToday ? { scale: 0.8, opacity: 0 } : { scale: 1, opacity: 1 }}
                            animate={day.isToday ? {
                                scale: 1,
                                opacity: 1,
                                boxShadow: ["0 0 0 0px rgba(6, 182, 212, 0)", "0 0 0 4px rgba(6, 182, 212, 0.5)", "0 0 0 0px rgba(6, 182, 212, 0)"]
                            } : {
                                scale: 1,
                                opacity: 1
                            }}
                            transition={day.isToday ? {
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                            } : {}}
                        >
                            <span className="text-xs font-medium">{day.dayNum}</span>

                            {/* Hover Tooltip */}
                            {
                                day.score > 0 && (
                                    <div className="absolute bottom-full mb-2 bg-gray-900 text-white text-xs px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
                                        {day.score} Picks • {day.pnl ? `${day.pnl > 0 ? '+' : ''}${day.pnl}%` : 'Pending'}
                                    </div>
                                )
                            }
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Recent History List */}
            <div className="mt-8 space-y-4">
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Recent Activity</h3>
                {history.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 font-light text-sm italic">
                        No swipes yet today. Start swiping to build your streak!
                    </div>
                ) : (
                    history.slice().reverse().map((item, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05 }}
                            className="flex items-center justify-between p-4 bg-white rounded-2xl shadow-sm border border-gray-100"
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.action === 'BUY_LIST' ? 'bg-green-50 text-green-600' :
                                    item.action === 'SHORTLIST' ? 'bg-yellow-50 text-yellow-600' :
                                        'bg-red-50 text-red-500'
                                    }`}>
                                    {item.action === 'BUY_LIST' ? <Check size={18} /> :
                                        item.action === 'SHORTLIST' ? <CalIcon size={18} /> :
                                            <X size={18} />}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900">{item.ticker}</div>
                                    <div className="text-xs text-gray-400 capitalize">{item.action.replace('_', ' ').toLowerCase()}</div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs text-gray-400">Just now</div>
                            </div>
                        </motion.div>
                    ))
                )}
            </div>
        </div >
    );
};

export default PerformanceCalendar;
